/**
 * Juni-2026 (User-Wunsch) — Inventur-Verlauf / Archiv.
 *
 * Liste der abgeschlossenen Inventur-Rundgänge (Datum, Modus, Soll/Ist/Fehlend)
 * mit „Protokoll (PDF) erneut laden" pro Eintrag (revisionssicher fürs Audit).
 */
import { useState, useEffect } from "react";
import { X, Loader2, Archive, FileDown, Car, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { previewPdfResponse } from "../../lib/pdfPreview";
import { Button } from "../ui/button";

const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso; } };
const MODE_LABEL = { full: "Fahrzeug + Schlüssel", papers: "Fahrzeugpapiere" };

export default function InventoryWalkArchiveModal({ onClose }) {
    const [items, setItems] = useState(null);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/dealer/inventory-walks?limit=100")
            .then((r) => { if (alive) setItems(r.data?.items || []); })
            .catch(() => { if (alive) setItems([]); });
        return () => { alive = false; };
    }, []);

    const openPdf = async (id) => {
        setBusyId(id);
        try {
            const r = await api.get(`/dealer/inventory-walks/${id}/protocol.pdf`, { responseType: "blob" });
            previewPdfResponse(r, `Inventur-Protokoll_${id.slice(0, 8)}.pdf`);
        } catch {
            toast.error("Protokoll konnte nicht geladen werden.");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto"
            data-testid="inventory-walk-archive-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div className="w-full max-w-2xl mt-10 bg-white border border-gray-200 rounded-sm shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-[#111827] text-white rounded-t-sm">
                    <h2 className="font-display text-lg font-black flex items-center gap-2">
                        <Archive className="w-5 h-5 text-[#FFCC00]" /> Inventur-Verlauf
                    </h2>
                    <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" aria-label="Schließen" data-testid="inventory-walk-archive-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-5 max-h-[65vh] overflow-y-auto">
                    {items === null && <div className="text-center py-8 text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>}
                    {items && items.length === 0 && (
                        <div className="text-center py-8 text-gray-500 text-sm" data-testid="inventory-walk-archive-empty">
                            Noch keine abgeschlossenen Rundgänge archiviert.
                        </div>
                    )}
                    <ul className="space-y-2">
                        {(items || []).map((w) => {
                            const fehlend = Math.max(0, (w.total || 0) - (w.complete || 0));
                            const Icon = w.mode === "papers" ? FileText : Car;
                            return (
                                <li key={w.id} className="border border-gray-200 rounded-sm p-3 flex items-start justify-between gap-3" data-testid={`inventory-walk-archive-row-${w.id}`}>
                                    <div className="min-w-0">
                                        <div className="text-sm font-bold text-[#111827] flex items-center gap-1.5">
                                            <Icon className="w-4 h-4 text-[#FFCC00]" /> {MODE_LABEL[w.mode] || w.mode}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5">{fmtDT(w.finished_at)} · {w.counter || "—"}</div>
                                        <div className="text-[11px] mt-1 flex items-center gap-3">
                                            <span className="text-gray-600">Soll <strong className="text-[#111827]">{w.total || 0}</strong></span>
                                            <span className="text-emerald-700">Ist <strong>{w.complete || 0}</strong></span>
                                            <span className={fehlend > 0 ? "text-red-600" : "text-gray-500"}>Fehlend <strong>{fehlend}</strong></span>
                                        </div>
                                    </div>
                                    <Button type="button" onClick={() => openPdf(w.id)} disabled={busyId === w.id}
                                        variant="outline" className="h-8 rounded-sm text-[11px] font-bold uppercase tracking-wider shrink-0"
                                        data-testid={`inventory-walk-archive-pdf-${w.id}`}>
                                        {busyId === w.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><FileDown className="w-3.5 h-3.5 mr-1" /> Protokoll</>}
                                    </Button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
}
